import React, { useRef, useState, useCallback, useId } from 'react';
import { uploadFile, UploadedFileInfo } from '../../services/api';

export interface S3FileUploadProps {
  token: string;
  documentType: string;
  label?: string;
  description?: string;
  hint?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  onUploadComplete?: (fileInfo: UploadedFileInfo) => void;
  onUploadError?: (message: string) => void;
  onFileRemoved?: () => void;
  containerClassName?: string;
}

interface UploadState {
  file: File | null;
  status: 'idle' | 'uploading' | 'success' | 'error';
  progress: number;
  error: string | null;
  uploadedInfo: UploadedFileInfo | null;
}

export const S3FileUpload: React.FC<S3FileUploadProps> = ({
  token,
  documentType,
  label,
  description,
  hint,
  error: externalError,
  disabled = false,
  required = false,
  onUploadComplete,
  onUploadError,
  onFileRemoved,
  containerClassName = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const statusId = useId();
  const [uploadState, setUploadState] = useState<UploadState>({
    file: null,
    status: 'idle',
    progress: 0,
    error: null,
    uploadedInfo: null,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const validateFile = useCallback((file: File): string | null => {
    // Extensões permitidas: PDF, imagens e planilhas Excel
    const allowedExtensions = [
      // Documentos
      '.pdf',
      // Imagens
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
      // Planilhas Excel
      '.xls', '.xlsx', '.xlsm', '.csv',
    ];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      return `Extensão não permitida: ${fileExtension}. Tipos aceitos: PDF, imagens (JPG, PNG, GIF) e planilhas (XLS, XLSX, CSV).`;
    }

    // Verificar tamanho (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return `Arquivo muito grande: ${formatFileSize(file.size)}. Tamanho máximo: 10MB`;
    }

    return null;
  }, []);

  const handleUpload = useCallback(
    async (file: File) => {
      // Validar arquivo
      const validationError = validateFile(file);
      if (validationError) {
        setUploadState({
          file,
          status: 'error',
          progress: 0,
          error: validationError,
          uploadedInfo: null,
        });
        onUploadError?.(validationError);
        return;
      }

      // Iniciar upload
      setUploadState({
        file,
        status: 'uploading',
        progress: 0,
        error: null,
        uploadedInfo: null,
      });

      try {
        const fileInfo = await uploadFile(token, file, documentType, (progress) => {
          setUploadState((prev) => ({ ...prev, progress }));
        });

        setUploadState({
          file,
          status: 'success',
          progress: 100,
          error: null,
          uploadedInfo: fileInfo,
        });

        onUploadComplete?.(fileInfo);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer upload';
        setUploadState({
          file,
          status: 'error',
          progress: 0,
          error: errorMessage,
          uploadedInfo: null,
        });
        onUploadError?.(errorMessage);
      }
    },
    [token, documentType, validateFile, onUploadComplete, onUploadError]
  );

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length > 0) {
        handleUpload(fileArray[0]);
      }
    },
    [handleUpload]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || uploadState.status === 'uploading') return;

      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        handleFiles(files);
      }
    },
    [disabled, uploadState.status, handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      // Reset input para permitir selecionar o mesmo arquivo novamente
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleClick = () => {
    if (!disabled && uploadState.status !== 'uploading') {
      inputRef.current?.click();
    }
  };

  const removeFile = () => {
    setUploadState({
      file: null,
      status: 'idle',
      progress: 0,
      error: null,
      uploadedInfo: null,
    });
    onFileRemoved?.();
  };

  const displayError = externalError || uploadState.error;
  const isUploading = uploadState.status === 'uploading';
  const hasFile = uploadState.file !== null;

  return (
    <div className={`mb-4 ${containerClassName}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}

      {/* Drop zone - mostrar apenas se não tiver arquivo */}
      {!hasFile && (
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={label ? `Enviar arquivo para ${label}` : 'Enviar arquivo'}
          aria-disabled={disabled}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
          className={`
            relative border-2 border-dashed rounded-lg p-6
            transition-all duration-200 cursor-pointer
            ${isDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
            ${displayError ? 'border-red-500' : ''}
          `}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.xls,.xlsx,.xlsm,.csv"
            onChange={handleInputChange}
            disabled={disabled}
            className="hidden"
          />

          <div className="text-center">
            <svg
              className={`mx-auto h-12 w-12 ${isDragging ? 'text-green-500' : 'text-gray-400'}`}
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <div className="mt-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium text-green-600">Clique para enviar</span>
                {' ou arraste e solte'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                PDF, imagens ou planilhas Excel (max. 10MB)
              </p>
            </div>
          </div>
        </div>
      )}

      <div id={statusId} aria-live="polite" aria-atomic="true" className="sr-only">
        {uploadState.status === 'uploading' && `Enviando arquivo ${uploadState.file?.name ?? ''}: ${uploadState.progress}%`}
        {uploadState.status === 'success' && `Arquivo ${uploadState.file?.name ?? ''} enviado com sucesso`}
        {uploadState.status === 'error' && `Erro ao enviar arquivo ${uploadState.file?.name ?? ''}: ${uploadState.error ?? ''}`}
      </div>

      {hasFile && uploadState.file && (
        <div
          className={`
            p-4 rounded-lg border
            ${uploadState.status === 'success' ? 'bg-green-50 border-green-200' : ''}
            ${uploadState.status === 'error' ? 'bg-red-50 border-red-200' : ''}
            ${uploadState.status === 'uploading' ? 'bg-blue-50 border-blue-200' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* File Icon baseado no tipo */}
              {(() => {
                const ext = uploadState.file.name.split('.').pop()?.toLowerCase();
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '');
                const isExcel = ['xls', 'xlsx', 'xlsm', 'csv'].includes(ext || '');

                if (isImage) {
                  return (
                    <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  );
                }
                if (isExcel) {
                  return (
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                  );
                }
                // PDF ou outro
                return (
                  <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                );
              })()}
              <div>
                <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                  {uploadState.file.name}
                </p>
                <p className="text-xs text-gray-500">{formatFileSize(uploadState.file.size)}</p>
              </div>
            </div>

            {/* Status indicator / Remove button */}
            <div className="flex items-center space-x-2">
              {uploadState.status === 'success' && (
                <span className="flex items-center text-green-600 text-sm">
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Enviado
                </span>
              )}

              {uploadState.status === 'error' && (
                <span className="flex items-center text-red-600 text-sm">
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Erro
                </span>
              )}

              {!isUploading && (
                <button
                  type="button"
                  aria-label={`Remover arquivo ${uploadState.file.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          {isUploading && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Enviando...</span>
                <span>{uploadState.progress}%</span>
              </div>
              <div
                className="w-full bg-gray-200 rounded-full h-2"
                role="progressbar"
                aria-valuenow={uploadState.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progresso do upload"
              >
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadState.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {displayError && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {displayError}
        </p>
      )}

      {hint && !displayError && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
    </div>
  );
};

export default S3FileUpload;
